import api from './api';

class EventTypeService {
  // Public endpoints - no authentication required
  async getPublicEventTypes() {
    try {
      const response = await api.get('/public/event-types');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching public event types:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch event types'
      };
    }
  }

  async getPublicCities() {
    try {
      const response = await api.get('/public/event-types/cities');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching cities:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch cities'
      };
    }
  }

  async getPublicEvents() {
    try {
      const response = await api.get('/public/events');
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching public events:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || 'Failed to fetch events'
      };
    }
  }

  async getVolunteerEvents(city) {
    try {
      const params = city ? `?city=${encodeURIComponent(city)}` : '';
      const response = await api.get(`/public/events/volunteer${params}`);
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching volunteer events:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || 'Failed to fetch events'
      };
    }
  }

  async getPublicAvailability(eventTypeId, date) {
    try {
      const response = await api.get(`/public/event-types/${eventTypeId}/availability?date=${encodeURIComponent(date)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching availability:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch availability'
      };
    }
  }

  async getPublicBlockedDates(eventTypeId, startDate, endDate) {
    try {
      const response = await api.get(`/public/event-types/${eventTypeId}/blocked-dates?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching public blocked dates:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch blocked dates'
      };
    }
  }

  // Admin endpoints - require authentication
  async createEventType(eventTypeData) {
    try {
      const response = await api.post('/admin/event-types', eventTypeData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating event type:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create event type'
      };
    }
  }

  async getEventTypes() {
    try {
      const response = await api.get('/admin/event-types?includeBookingCounts=true');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching event types:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch event types'
      };
    }
  }

  async updateEventType(eventTypeId, eventTypeData) {
    try {
      const response = await api.put(`/admin/event-types/${eventTypeId}`, eventTypeData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating event type:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update event type'
      };
    }
  }

  async deleteEventType(eventTypeId) {
    try {
      const response = await api.delete(`/admin/event-types/${eventTypeId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error deleting event type:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete event type'
      };
    }
  }

  // Event-type-specific blocked dates
  async getEventTypeBlockedDates(eventTypeId, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = queryString 
        ? `/admin/event-types/${eventTypeId}/blocked-dates?${queryString}`
        : `/admin/event-types/${eventTypeId}/blocked-dates`;
      const response = await api.get(url);
      return {
        success: true,
        data: response // Backend returns { success, count, data } directly
      };
    } catch (error) {
      console.error('Error fetching event type blocked dates:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch blocked dates'
      };
    }
  }

  async createEventTypeBlockedDate(eventTypeId, blockedDateData) {
    try {
      const response = await api.post(`/admin/event-types/${eventTypeId}/blocked-dates`, blockedDateData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating event type blocked date:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create blocked date'
      };
    }
  }

  // Monthly Caps Statistics
  async getMonthlyStats(eventTypeId, monthsAhead = 6) {
    try {
      const response = await api.get(`/admin/event-types/${eventTypeId}/monthly-stats?monthsAhead=${monthsAhead}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch monthly statistics'
      };
    }
  }
}

export default new EventTypeService();
